import React, { useMemo } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { Plus } from 'lucide-react';
import calendarIcon from '@/assets/icons/calendar.svg';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useInstructorClasses } from '@/features/calendar/hooks/useInstructorClasses';
import { useSmartDateSelection } from '@/features/calendar/hooks/useSmartDateSelection';
import { InstructorSlotsSection } from './InstructorSlotsSection';
import { isSameDay, format, addDays } from 'date-fns';
import { useAvailabilitySlots } from '@/features/calendar/hooks/useAvailabilitySlots';
import { useAgendaConfig } from '@/features/calendar/hooks/useAgendaConfig';
import { useAuth } from '@/contexts/AuthContext';

const NextClassesSection: React.FC = () => {
  const { t, currentLanguage } = useI18n();
  const location = useLocation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { config: agendaConfig } = useAgendaConfig();
  
  // Use smart date selection (today or next available working day)
  const selectedDate = useSmartDateSelection();
  // Fetch future classes (up to 3) for the slider combination logic
  const { classes, isLoading, error, refetch } = useInstructorClasses(
    selectedDate,
    undefined,
    { fetchFuture: true, limit: 3 }
  );

  // Buscar configuração da agenda
  const effectiveConfig = (agendaConfig && typeof agendaConfig === 'object' && !Array.isArray(agendaConfig)) ? agendaConfig : {
    instructor_id: user?.id || '',
    working_days: ['mon', 'tue', 'wed', 'thu', 'fri'],
    working_hours: {
      mon: { start: '08:00', end: '18:00' },
      tue: { start: '08:00', end: '18:00' },
      wed: { start: '08:00', end: '18:00' },
      thu: { start: '08:00', end: '18:00' },
      fri: { start: '08:00', end: '18:00' }
    },
    default_duration: 60,
    is_public: false
  };

  // Calculate how many slots we need to show to complete up to 3 items total
  // Logic: [aulas (máx 3) + slots (caso não tenha aulas marcadas e número de aulas < 3)]
  const maxItems = 3;
  const classesToShow = useMemo(() => {
    // Show up to maxItems classes
    return classes.slice(0, maxItems);
  }, [classes]);

  const slotsNeeded = useMemo(() => {
    // Calculate how many slots we need to complete up to maxItems
    const classesCount = classesToShow.length;
    return Math.max(0, maxItems - classesCount);
  }, [classesToShow]);

  // Calculate working days to check for slots
  // IMPORTANT: When selectedDate is tomorrow (because today is closed):
  // - We show selectedDate once (Dec 03)
  // - If selectedDate has no slots, InstructorSlotsSection shows nextWorkingDay (Dec 04)
  // - To avoid duplication, daysToCheckForSlots should start from selectedDate + 2 (Dec 05)
  // When selectedDate is today:
  // - daysToCheckForSlots starts from selectedDate + 1 (tomorrow)
  const daysToCheckForSlots = useMemo(() => {
    const workingDays = effectiveConfig.working_days || ['mon', 'tue', 'wed', 'thu', 'fri'];
    const days: Date[] = [];
    const today = new Date();
    const isSelectedDateToday = isSameDay(selectedDate, today);
    
    // If selectedDate is today, start from tomorrow (selectedDate + 1)
    // If selectedDate is tomorrow, start from day after tomorrow (selectedDate + 2)
    // This avoids duplication when InstructorSlotsSection shows nextWorkingDay
    let currentDay = isSelectedDateToday ? addDays(selectedDate, 1) : addDays(selectedDate, 2);
    let attempts = 0;
    const maxAttempts = 14; // Check up to 2 weeks ahead
    
    while (days.length < slotsNeeded && attempts < maxAttempts) {
      // Use format from date-fns for consistent formatting across environments
      const dayName = format(currentDay, 'EEE').toLowerCase();
      
      if (workingDays.includes(dayName)) {
        days.push(new Date(currentDay));
      }
      
      currentDay = addDays(currentDay, 1);
      attempts++;
    }
    
    return days;
  }, [selectedDate, effectiveConfig, slotsNeeded]);

  // Forçar recarregamento quando o usuário retorna para a página Home
  useEffect(() => {
    if (location.pathname === '/home') {
      // Invalidar queries relacionadas para forçar recarregamento
      queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
      queryClient.invalidateQueries({ queryKey: ['instructor-highlights'] });
      
      // Forçar refetch imediato
      refetch();
    }
  }, [location.pathname, queryClient, refetch]);

  const handleSeeAll = () => {
    navigate('/calendar');
  };

  const handleAddClass = () => {
    navigate('/calendar');
    // TODO: Open Add Class modal
  };

  // Função para traduzir o tipo de aula
  const getClassTypeTranslation = (type: string) => {
    // Normalize type to lowercase for matching
    const normalizedType = type.toLowerCase().trim();
    
    const translations: Record<string, string> = {
      'private': t('home.next_classes.private_class'),
      'private class': t('home.next_classes.private_class'),
      'group': t('home.next_classes.group_class'),
      'group class': t('home.next_classes.group_class'),
      'seminar': 'Seminário',
      'competition': 'Competição',
      'open_mat': 'Open Mat',
    };
    return translations[normalizedType] || type;
  };

  // Função para formatar a data
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayName = dayNames[date.getDay()];
    return `${day} ${dayName}`;
  };

  // Função para formatar o horário
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(currentLanguage === 'pt' ? 'pt-BR' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  if (isLoading) {
    return (
      <div className="mb-8">
        <h2 className="text-sm font-bold text-white font-roboto mb-4">
          {t('home.next_classes.title')}
        </h2>
        <div className="text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-8">
        <h2 className="text-sm font-bold text-white font-roboto mb-4">
          {t('home.next_classes.title')}
        </h2>
        <div className="text-red-400">{t('home.next_classes.error_loading')}</div>
      </div>
    );
  }

  // Always show the section, even if there are no classes
  // The slots section may have available slots even without scheduled classes

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold text-white font-roboto">
          {t('home.next_classes.title')} {selectedDate && !isSameDay(selectedDate, new Date()) && (
            <span className="text-xs font-normal text-gray-400 ml-2">
              ({format(selectedDate, 'MMM dd')})
            </span>
          )}
        </h2>
        <button
          onClick={handleSeeAll}
          className="text-xs text-white underline hover:text-gray-300 transition-colors"
        >
          {t('home.next_classes.see_all')}
        </button>
      </div>

      <div className="relative max-w-full mx-auto">
        <div className="w-full overflow-x-auto pb-2 scrollbar-hide">
          <div className="flex space-x-4">
            {/* Render classes (up to maxItems) */}
            {classesToShow.map((classItem) => {
              const dateInfo = formatDate(classItem.start_time);
              const startTime = formatTime(classItem.start_time);
              const endTime = formatTime(classItem.end_time);
              
              return (
                <div key={classItem.id} className="flex-shrink-0 w-[225px] bg-[rgb(45,36,36)] rounded-lg p-4 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
                  <div className="mb-3">
                    <div className="text-sm font-bold text-white mb-2 font-roboto">
                      {getClassTypeTranslation(classItem.type)} - {classItem.modality}
                    </div>
                    <div className="text-xs text-gray-300 font-roboto flex items-center space-x-2">
                      <img src={calendarIcon} alt={t('common.calendar')} className="w-4 h-4" />
                      <span>{dateInfo} - {startTime} to {endTime}</span>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Render slots to complete up to maxItems total */}
            {/* Slider logic: [aulas (máx 3) + slots (caso não tenha aulas marcadas e número de aulas < 3)] */}
            {/* Only show slots if we need them to complete the slider */}
            {/* IMPORTANT: Show selectedDate first if it's not today (it's tomorrow because today is closed) */}
            {/* Then show daysToCheckForSlots which starts from selectedDate + 1 */}
            {/* CRITICAL: Pass classes filtered by date to InstructorSlotsSection so slots are calculated correctly */}
            {slotsNeeded > 0 && (() => {
              const today = new Date();
              const isSelectedDateToday = isSameDay(selectedDate, today);
              const slotsToRender: JSX.Element[] = [];
              
              // Helper function to filter classes for a specific date
              const getClassesForDate = (targetDate: Date): any[] => {
                const targetDateStr = format(targetDate, 'yyyy-MM-dd');
                return classes.filter(classItem => {
                  const classDate = new Date(classItem.start_time);
                  const classDateStr = format(classDate, 'yyyy-MM-dd');
                  return classDateStr === targetDateStr;
                });
              };
              
              // If selectedDate is NOT today (it's tomorrow), show it first
              // This ensures we show tomorrow once when today is closed
              if (!isSelectedDateToday && slotsToRender.length < slotsNeeded) {
                const dateKey = format(selectedDate, 'yyyy-MM-dd');
                const classesForDate = getClassesForDate(selectedDate);
                slotsToRender.push(
                  <InstructorSlotsSection 
                    key={`slots-selected-${dateKey}`} 
                    classes={classesForDate} 
                    selectedDate={selectedDate} 
                  />
                );
              }
              
              // Then show days from daysToCheckForSlots (which starts from selectedDate + 1)
              daysToCheckForSlots.forEach((date, index) => {
                if (slotsToRender.length >= slotsNeeded) return;
                
                const dateKey = format(date, 'yyyy-MM-dd');
                const classesForDate = getClassesForDate(date);
                slotsToRender.push(
                  <InstructorSlotsSection 
                    key={`slots-${dateKey}-${index}`} 
                    classes={classesForDate} 
                    selectedDate={date} 
                  />
                );
              });
              
              return slotsToRender;
            })()}
          </div>
        </div>
      </div>


    </div>
  );
};

export default NextClassesSection;
